import { StrictMode, lazy, Suspense } from 'react'
import { createRoot } from 'react-dom/client'
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom'
import './index.css'
import LandingPage from './LandingPage.tsx'
import LoginPage from './LoginPage.tsx'
import RegisterPage from './RegisterPage.tsx'
import NotFoundPage from './NotFoundPage.tsx'
import { AuthProvider } from './AuthContext.tsx'
import ProtectedRoute from './ProtectedRoute.tsx'
import AppLayout from './AppLayout.tsx'
import PrintView from './obs/PrintView.tsx'  // import statique : Chromium print ne doit attendre aucun chunk lazy
// Même raison pour la page imprimée de l'ABS (CDC du 14/10/2026, § 7.5).
import ImpressionAbs from './etudes/abs/ImpressionAbs.tsx'
import GardeConsultant from './etudes/GardeConsultant.tsx'
import ErrorBoundary from './ErrorBoundary.tsx'
import ScrollToTop from './components/ScrollToTop.tsx'
import AvisSession from './components/AvisSession.tsx'
import { ToastProvider } from './ToastContext.tsx'

// Lazy-loaded heavy components (recharts, maplibre-gl, etc.)
// 🪦 Ancien site catalogue coupé le 11/07/2026 (décisions 20-21/06, date fixée par Marc) :
// App (explorateur /commune), CatalogueEtudes, tunnels Flash/Premium/CB, PricingPage flash
// et LandingPage 3 offres ne sont plus routés — code figé dans le tag ancien-site-final-20260711,
// briques réutilisables cataloguées dans docs/cahier-des-charges/NOTE-harvest-ancien-site.md.
const CGVPage = lazy(() => import('./CGVPage.tsx'))
const PrivacyPage = lazy(() => import('./PrivacyPage.tsx'))
const EspaceClient = lazy(() => import('./EspaceClient.tsx'))
const AdminDashboard = lazy(() => import('./AdminDashboard.tsx'))
// Maquettes de design (Marc 10/10) : route statique, classée au-dessus de /admin/:vue, SANS
// AppLayout — la toile doit être vierge pour que seule la direction de design parle.
const DesignRoutes = lazy(() => import('./admin/design/DesignRoutes.tsx'))
const GrapheFlux = lazy(() => import('./admin/donnees/GrapheFlux.tsx'))
const AboutPage = lazy(() => import('./AboutPage.tsx'))
const ContactPage = lazy(() => import('./ContactPage.tsx'))
const FAQPage = lazy(() => import('./FAQPage.tsx'))
const CommandeConfirmeePage = lazy(() => import('./CommandeConfirmeePage.tsx'))
// D13 (24/10) : le lien de paiement par carte, envoyé après validation du devis par l'équipe.
const DevisPayerPage = lazy(() => import('./DevisPayerPage.tsx'))
const MentionsLegalesPage = lazy(() => import('./MentionsLegalesPage.tsx'))
// Déclaration d'accessibilité RGAA (Q7, lot F 07/09) — page légale publique, pré-rendue.
const AccessibilitePage = lazy(() => import('./AccessibilitePage.tsx'))
const ForgotPasswordPage = lazy(() => import('./ForgotPasswordPage.tsx'))
const CollectivitesPage = lazy(() => import('./CollectivitesPage.tsx'))
const ResetPasswordPage = lazy(() => import('./ResetPasswordPage.tsx'))
const InvitationPage = lazy(() => import('./InvitationPage.tsx'))
// Lot 0 : le lien de confirmation de l'adresse (48 h, usage unique).
const VerificationAdressePage = lazy(() => import('./VerificationAdressePage.tsx'))
// Observatoire territorial (refonte CDC) — shell topbar+sidebar, écrans par thématique
const ObsLayout = lazy(() => import('./obs/ObsLayout.tsx'))
const SyntheseView = lazy(() => import('./obs/SyntheseView.tsx'))
const ComparaisonView = lazy(() => import('./obs/ComparaisonView.tsx'))
const ThemeView = lazy(() => import('./obs/ThemeView.tsx'))
const MembresView = lazy(() => import('./obs/MembresView.tsx'))
const PartagesView = lazy(() => import('./obs/PartagesView.tsx'))
const EchangesView = lazy(() => import('./obs/EchangesView.tsx'))
const BilanFinancierView = lazy(() => import('./obs/etudes/BilanFinancierView.tsx'))
// Partage par lien (CDC E2, 03/10) : la page d'un lien, hors de l'observatoire (aucun menu).
const PagePartage = lazy(() => import('./obs/partage/PagePartage.tsx'))
// Chargée à la demande, comme les autres vues : MapLibre ne pèse sur personne qui n'ouvre pas la carte.
const CartoView = lazy(() => import('./obs/carto/CartoView.tsx'))
const EpciRoutes = lazy(() => import('./obs/epci/EpciRoutes.tsx'))
const EpciOffrePage = lazy(() => import('./EpciOffrePage.tsx'))
const EptOffrePage = lazy(() => import('./EpciOffrePage.tsx').then(m => ({ default: m.EptOffrePage })))
const EpciLandingPage = lazy(() => import('./EpciLandingPage.tsx'))
const LabIndicateursView = lazy(() => import('./lab/LabIndicateursView.tsx'))
const LabPyramideView = lazy(() => import('./lab/LabPyramideView.tsx'))
const SigView = lazy(() => import('./sig/SigView.tsx'))
// L'ABS sur le site (CDC du 14/10/2026, § 8) : hors de l'observatoire, sans menu de commune.
const EtudesLayout = lazy(() => import('./etudes/EtudesLayout.tsx'))
const EtudesAccueil = lazy(() => import('./etudes/EtudesAccueil.tsx'))
const EtudeView = lazy(() => import('./etudes/abs/EtudeView.tsx'))


function LegacyObsRedirect() {
  const { codeCommune } = useParams<{ codeCommune: string }>()
  return <Navigate to={`/o/${codeCommune}/synthese`} replace />
}

function LoadingSkeleton() {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header skeleton */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between max-w-7xl mx-auto">
          <div className="h-8 w-48 bg-gray-200 rounded animate-pulse" />
          <div className="flex gap-4">
            <div className="h-8 w-24 bg-gray-200 rounded animate-pulse" />
            <div className="h-8 w-24 bg-gray-200 rounded animate-pulse" />
          </div>
        </div>
      </div>

      {/* Content skeleton */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* Title */}
        <div className="h-10 w-80 bg-gray-200 rounded animate-pulse mb-2" />
        <div className="h-5 w-48 bg-gray-200 rounded animate-pulse mb-8" />

        {/* Tabs skeleton */}
        <div className="flex gap-2 mb-8">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <div key={i} className="h-10 w-28 bg-gray-200 rounded-lg animate-pulse" />
          ))}
        </div>

        {/* Cards grid skeleton */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="bg-white rounded-xl p-6 border border-gray-100">
              <div className="h-4 w-24 bg-gray-200 rounded animate-pulse mb-3" />
              <div className="h-8 w-20 bg-gray-200 rounded animate-pulse mb-2" />
              <div className="h-3 w-32 bg-gray-200 rounded animate-pulse" />
            </div>
          ))}
        </div>

        {/* Chart skeletons */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white rounded-xl p-6 border border-gray-100">
            <div className="h-5 w-40 bg-gray-200 rounded animate-pulse mb-4" />
            <div className="h-64 bg-gray-100 rounded-lg animate-pulse" />
          </div>
          <div className="bg-white rounded-xl p-6 border border-gray-100">
            <div className="h-5 w-40 bg-gray-200 rounded animate-pulse mb-4" />
            <div className="h-64 bg-gray-100 rounded-lg animate-pulse" />
          </div>
        </div>
      </div>
    </div>
  )
}

// 🪦 CataloguePage / SouscrirePage / SouscrirePremiumPage / helpers slug : retirés avec
// l'ancien site (tag ancien-site-final-20260711). Les URLs sont redirigées vers /o.

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <AuthProvider>
      <ToastProvider>
      <BrowserRouter>
        {/* Doit être DANS le Router (il lit la location) et HORS du Suspense : sinon la
            remise à zéro attend le chargement du chunk lazy de la page d'arrivée. */}
        <ScrollToTop />
        {/* Session interrompue ou expirée (CDC GBM du 27/10, SE-1 et SE-3) : un avis pour tout le site. */}
        <AvisSession />
        <ErrorBoundary>
          <Suspense fallback={<LoadingSkeleton />}>
            {/* Un consultant (rôle analyst) n'ouvre que ses études : toute autre page le ramène à /etudes. */}
            <GardeConsultant>
            <Routes>
              {/* Landing riche (décision C tranchée par Marc 12/07) : vitrine mono-produit,
                  angle ROI (agrégation = ~1 ETP économisé). Remplace l'intérim / → /o. */}
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/invitation" element={<InvitationPage />} />
              <Route path="/verifier-adresse" element={<VerificationAdressePage />} />
              {/* 🪦 ancien explorateur : /commune/:code → observatoire de la commune */}
              <Route path="/commune/:codeCommune" element={<LegacyObsRedirect />} />
              {/* Observatoire (refonte CDC) : /o redirige vers la vitrine, /o/:insee = shell sidebar */}
              <Route path="/o" element={<Navigate to="/" replace />} />
              {/* page imprimable (Chromium headless — auth par pdf_token éphémère) */}
              <Route path="/o/:insee/print" element={<PrintView />} />
              <Route path="/p/:jeton" element={<PagePartage />} />
              {/* Études de l'équipe et des consultants (ABS, CDC du 14/10/2026) : la page imprimée (jeton
                  court du backend, sans session), l'accueil des études et la page d'une étude. */}
              <Route path="/etudes/:id/impression" element={<ImpressionAbs />} />
              <Route path="/etudes" element={<ProtectedRoute requireEtudes><EtudesLayout><EtudesAccueil /></EtudesLayout></ProtectedRoute>} />
              <Route path="/etudes/:id" element={<ProtectedRoute requireEtudes><EtudesLayout><EtudeView /></EtudesLayout></ProtectedRoute>} />
              {/* Le bilan financier du mandat, modifiable par un consultant affecté (CDC du 15/10) : la même page que
                  celle de l'observatoire, servie par l'API des études. */}
              <Route path="/etudes/bilan/:insee" element={<ProtectedRoute requireEtudes><EtudesLayout><BilanFinancierView /></EtudesLayout></ProtectedRoute>} />
              {/* Vue EPCI (socle, 26/09) — même construction que /o/:insee, valeurs agrégées */}
              <Route path="/o/epci/:siren/*" element={<EpciRoutes />} />
              <Route path="/sig" element={<ProtectedRoute requireCarteNationale><SigView /></ProtectedRoute>} />
              <Route path="/o/:insee" element={<ObsLayout />}>
                <Route index element={<Navigate to="synthese" replace />} />
                <Route path="synthese" element={<SyntheseView />} />
                <Route path="comparaison" element={<ComparaisonView />} />
                <Route path="t/:themeId" element={<ThemeView />} />
                {/* Cartographie dynamique (CDC F, 21/09) : la carte comme porte d'entrée. */}
                <Route path="carte" element={<CartoView />} />
                <Route path="membres" element={<MembresView />} />
                <Route path="partages" element={<PartagesView />} />
                <Route path="echanges" element={<EchangesView />} />
                {/* Études de la commune (CDC du 14/10/2026) : le bilan financier du mandat, en ligne et en PDF. */}
                <Route path="etudes" element={<Navigate to="bilan-financier" replace />} />
                <Route path="etudes/bilan-financier" element={<BilanFinancierView />} />
              </Route>
              {/* redirections legacy v0 */}
              <Route path="/observatoire" element={<Navigate to="/" replace />} />
              <Route path="/observatoire/:codeCommune" element={<LegacyObsRedirect />} />
              {/* 🪦 catalogue d'études : vente à la carte close le 21/06 → l'observatoire */}
              <Route path="/catalogue" element={<Navigate to="/" replace />} />
              <Route path="/catalogue/:studyCode" element={<Navigate to="/" replace />} />
              <Route path="/catalogue/:studyCode/:communeSlug" element={<Navigate to="/" replace />} />
              {/* Espace client USER-SCOPÉ (lot C, 07/09) : /espace ne lit que /api/me/* — abonnements, devis
                  et commandes (identité de facturation #87), factures, compte (suppression RGPD). Ouvert à
                  tout compte connecté. L'ancienne vue (routes admin /api/orders, /api/reports) = /admin/archives. */}
              <Route path="/espace" element={<ProtectedRoute><AppLayout><EspaceClient /></AppLayout></ProtectedRoute>} />
              {/* Une URL par vue (Marc 02/08) : /admin/ventes et /admin/crm sont
                  partageables, mettables en favori, et le bouton Retour navigue entre
                  elles. /admin nu redirige vers les ventes. */}
              <Route path="/admin" element={<Navigate to="/admin/ventes" replace />} />
              <Route path="/admin/design/*" element={<ProtectedRoute requireAdmin><DesignRoutes /></ProtectedRoute>} />
              <Route path="/admin/donnees/graphe" element={<ProtectedRoute requireAdmin><AppLayout><GrapheFlux /></AppLayout></ProtectedRoute>} />
              <Route path="/admin/:vue" element={<ProtectedRoute requireAdmin><AppLayout><AdminDashboard /></AppLayout></ProtectedRoute>} />
              <Route path="/commande-confirmee" element={<CommandeConfirmeePage />} />
              <Route path="/devis/:reference/payer" element={<ProtectedRoute><DevisPayerPage /></ProtectedRoute>} />
              <Route path="/mentions-legales" element={<MentionsLegalesPage />} />
              <Route path="/accessibilite" element={<AccessibilitePage />} />
              {/* B4 : circuit d'achat public (devis, Chorus Pro, mandat administratif) */}
              <Route path="/collectivites" element={<CollectivitesPage />} />
              {/* Offre publique EPCI (P0-3) — vitrine générique + prix du territoire en ligne */}
              {/* Laboratoire des indicateurs maison (R1, 27/07) — ADMIN uniquement */}
              <Route path="/lab/:insee" element={<LabIndicateursView />} />
              <Route path="/lab/:insee/pyramide" element={<LabPyramideView />} />
              <Route path="/epci" element={<EpciLandingPage />} />
              <Route path="/epci/:siren" element={<EpciOffrePage />} />
              {/* Offre d'un EPT du Grand Paris (CDC du 29/10, § 4.2) : le même composant */}
              <Route path="/ept/:siren" element={<EptOffrePage />} />
              {/* Offre départementale retirée (Marc et Cyrille, 24/10) : autre échelle, contact. */}
              <Route path="/departement" element={<Navigate to="/contact" replace />} />
              <Route path="/departement/:code" element={<Navigate to="/contact" replace />} />
              {/* Board écoute client « Proposez un indicateur » */}
              <Route path="/proposer" element={<Navigate to="/" replace />} />
              {/* 🪦 tunnels CB : remplacés par le circuit devis → BC → activation */}
              <Route path="/souscrire" element={<Navigate to="/collectivites" replace />} />
              <Route path="/souscrire-premium" element={<Navigate to="/collectivites" replace />} />
              {/* 🪦 tarifs flash : la grille des paliers vit sur l'entrée observatoire */}
              <Route path="/pricing" element={<Navigate to="/#tarifs" replace />} />
              <Route path="/cgv" element={<CGVPage />} />
              <Route path="/confidentialite" element={<PrivacyPage />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/faq" element={<FAQPage />} />
              <Route path="/donnees-et-sources" element={<Navigate to="/" replace />} />
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
            </GardeConsultant>
          </Suspense>
        </ErrorBoundary>
      </BrowserRouter>
      </ToastProvider>
    </AuthProvider>
  </StrictMode>,
)
